import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import firebaseApp from "../firebase";
import { useAuth } from "../context/AuthContext";

const db = getFirestore(firebaseApp);

const SiteConfigPage = () => {
  const { currentUser } = useAuth();
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [iosSettings, setIosSettings] = useState({ iosPaymentEnabled: false, showIosExternalPaymentNotice: false });
  const [savingSetting, setSavingSetting] = useState(null);
  const [settingFeedback, setSettingFeedback] = useState(null);

  // Define fields that should only accept digits
  const numericFields = [
    "MockExamCoins",
    "assignmentSubmissionCost",
    "chatcoins",
    "coinPricePerUnit",
    "coins",
    "coinsRequired",
    "gpaCoins",
    "initialCoins",
    "projectTopicRequestCost",
  ];

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        const adminDocRef = doc(db, "adminPanel", "admin");
        const adminDocSnap = await getDoc(adminDocRef);
        if (adminDocSnap.exists()) {
          const data = adminDocSnap.data();
          setAdminData(data);
          setFormData(data);
          setIosSettings({ iosPaymentEnabled: data.iosPaymentEnabled === true, showIosExternalPaymentNotice: data.showIosExternalPaymentNotice === true });
        } else {
          console.error("No admin data found.");
        }
      } catch (error) {
        console.error("Error fetching admin data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  const isNumericField = (key) => {
    return numericFields.includes(key);
  };

  const validateInput = (name, value) => {
    if (isNumericField(name)) {
      if (!/^\d*$/.test(value)) {
        return "This field must contain only digits.";
      }
    }
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const error = validateInput(name, value);

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: error });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    // Check for any validation errors
    const hasErrors = Object.values(errors).some((error) => error !== "");
    if (hasErrors) {
      alert("Please fix the validation errors before submitting.");
      return;
    }
    try {
      const adminDocRef = doc(db, "adminPanel", "admin");
      const generalSettings = Object.fromEntries(Object.entries(formData).filter(([key]) => !["iosPaymentEnabled", "showIosExternalPaymentNotice", "updatedAt", "updatedBy"].includes(key)));
      await updateDoc(adminDocRef, generalSettings);
      alert("Admin data updated successfully.");
    } catch (error) {
      console.error("Error updating admin data:", error);
      alert("Failed to update admin data.");
    }
  };

  const updateIosSetting = async (key) => {
    if (savingSetting) return;
    if (currentUser?.isAdmin !== true) {
      setSettingFeedback({ type: "error", text: "Your account is not recognised as an admin. Sign out and sign in again." });
      return;
    }
    const nextValue = !iosSettings[key];
    setSavingSetting(key);
    setSettingFeedback(null);
    try {
      await updateDoc(doc(db, "adminPanel", "admin"), { [key]: nextValue, updatedAt: serverTimestamp(), updatedBy: currentUser.uid });
      setIosSettings((current) => ({ ...current, [key]: nextValue }));
      setSettingFeedback({ type: "success", text: "iOS payment setting saved." });
    } catch (error) {
      console.error("Error updating iOS payment setting:", error);
      setSettingFeedback({ type: "error", text: "Could not save the setting. Please try again." });
    } finally {
      setSavingSetting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        Site Configuration
      </h1>
      <section className="mb-8 rounded-lg border border-gray-200 p-5" aria-labelledby="ios-payment-settings-title">
        <h2 id="ios-payment-settings-title" className="text-lg font-bold text-gray-800 mb-1">iOS Payment Settings</h2>
        <p className="text-sm text-gray-500 mb-5">These controls affect iPhone and iPad users only. Android is not affected.</p>
        {[
          { key: "iosPaymentEnabled", label: "Use NC Wallet instead of Apple Pay", description: "OFF: iPhone and iPad users buy coins with Apple Pay (in-app purchase). ON: they pay by NC Wallet bank transfer instead and Apple Pay is not shown. Keep this OFF if you want Apple Pay." },
          { key: "showIosExternalPaymentNotice", label: "Show external payment notice", description: "Shows the notice text and link set below on the iOS coin purchase screen. Only applies when NC Wallet is ON." },
        ].map(({ key, label, description }) => (
          <div key={key} className="flex items-center justify-between py-4 border-t border-gray-100">
            <div className="pr-4"><div className="font-medium text-gray-800">{label}</div><div className="text-sm text-gray-500">{description}</div><div className="text-xs font-semibold mt-1">{key === "iosPaymentEnabled" ? `Currently ${iosSettings[key] ? "ON: iOS users see NC Wallet" : "OFF: iOS users see Apple Pay"}` : `Currently ${iosSettings[key] ? "on" : "off"}`}</div></div>
            <button type="button" role="switch" aria-checked={iosSettings[key]} disabled={!!savingSetting} onClick={() => updateIosSetting(key)} style={{ width: 48, height: 28, padding: 0 }} className={`relative block flex-shrink-0 rounded-full border-0 transition-colors ${iosSettings[key] ? "bg-blue-600" : "bg-gray-300"} ${savingSetting ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
              <span
                aria-hidden="true"
                className="absolute rounded-full bg-white shadow transition-all"
                style={{ top: 4, left: iosSettings[key] ? 24 : 4, width: 20, height: 20 }}
              />
              <span className="sr-only">{savingSetting === key ? "Saving" : label}</span>
            </button>
          </div>
        ))}
        {savingSetting && <p className="text-sm text-blue-600 mt-2" role="status">Saving…</p>}
        {settingFeedback && <p className={`text-sm mt-2 ${settingFeedback.type === "error" ? "text-red-600" : "text-green-600"}`} role="status">{settingFeedback.text}</p>}
      </section>
      <form onSubmit={handleFormSubmit} className="space-y-6">
        {adminData &&
          Object.keys(adminData).filter((key) => !["iosPaymentEnabled", "showIosExternalPaymentNotice", "updatedAt", "updatedBy"].includes(key)).map((key) => (
            <div
              key={key}
              className="grid grid-cols-1 gap-4 sm:grid-cols-3 items-center"
            >
              <label className="text-sm font-medium text-gray-700 capitalize">
                {key === "initialCoins"
                  ? "Coin Added After Registration"
                  : key === "coinPricePerUnit"
                  ? "Coin Price"
                  : key === "coinsRequired"
                  ? "Coins Required for Practice/Study"
                  : key.replace(/([A-Z])/g, " $1").trim()}
              </label>
              <div className="col-span-2">
                <input
                  type={isNumericField(key) ? "text" : "text"}
                  name={key}
                  value={formData[key] || ""}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 border ${
                    errors[key]
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  } rounded-md focus:outline-none focus:ring-2`}
                  placeholder={`Enter ${key.replace(/([A-Z])/g, " $1").trim()}`}
                />
                {errors[key] && (
                  <p className="mt-1 text-sm text-red-500">{errors[key]}</p>
                )}
              </div>
            </div>
          ))}
        <div className="flex justify-end">
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition duration-200"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default SiteConfigPage;
